import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  BuildingOffice2Icon,
  CheckCircleIcon,
  ChevronLeftIcon,
  DocumentTextIcon,
  FolderIcon,
  PlusIcon,
  SparklesIcon,
} from '@heroicons/react/20/solid';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import { Dialog, DialogTitle } from '@/components/ui/catalyst/dialog';
import { Z_INDEX_MODAL_DEEPLY_NESTED } from '@/components/ui/zIndex';
import { usePackageComponents } from '@/hooks/usePackageComponents';
import { cn } from '@/lib/utils';
import { useGetFirmQuery } from '@/services/api/firmsService';
import type { TemplateCategory as TemplateCategoryType } from '@/services/api/generated/templatesV2Service';
import type {
  PackageComponentSchema,
  PackageRequestInfo,
  StaticDocumentTag,
} from '@/services/api/hipaaPackageService';
import type { Blob as BlobType } from '@/services/types/blob-types';

import { useRequestPage } from '../contexts/RequestPageContext';
import type { PackageComponentsListProps } from '../types';
import {
  getSignedAuthorizationSelection,
  setSignedAuthorizationSelection,
} from '../utils/signedAuthorizationCache';
import { ComponentItem } from './ComponentItem';
import { DocumentSelector } from './DocumentSelector';
import { TemplateSearchList } from './TemplateSearchList';

/**
 * Component list for managing HIPAA package components
 * Uses context for package data and request info
 */
export const PackageComponentsList = ({
  selectedComponentId,
  onSelectComponent,
  onComponentsChange,
  showAddPanel,
  onShowAddPanel,
  packageMode,
  onOpenDocumentEditor,
}: PackageComponentsListProps) => {
  const { packageData, requestInfo, matterContext, matter } = useRequestPage();

  // Memoize components array (before early return)
  // Sort by order field to ensure consistent display
  const components = useMemo(
    () =>
      [...(packageData?.components || [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      ),
    [packageData?.components],
  );
  const packageId = packageData?.id || '';

  // Local state for optimistic updates
  const [localComponents, setLocalComponents] =
    useState<PackageComponentSchema[]>(components);

  // Sync local state with props
  useEffect(() => {
    setLocalComponents(components);
  }, [components]);

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
  );

  const handleRefetch = async () => {
    await onComponentsChange();
  };

  const { removeComponent, reorderComponents, isLoading } = usePackageComponents({
    packageId,
    requestInfo,
    components,
    onRefetch: handleRefetch,
  });

  // Early return after all hooks
  if (!packageData) return null;

  const isEditMode = packageMode === 'edit';

  const handleRemoveComponent = async (componentId: string) => {
    try {
      await removeComponent(componentId);
      onComponentsChange();

      // Clear selection if the removed component was selected
      if (componentId === selectedComponentId) {
        onSelectComponent(null);
      }

      toast.success('Component removed');
    } catch (error) {
      toast.error('Failed to remove component');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = localComponents.findIndex((c) => c.id === active.id);
    const newIndex = localComponents.findIndex((c) => c.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistic update: reorder immediately in local state
    const reordered = [...localComponents];
    const [movedItem] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, movedItem);
    setLocalComponents(reordered);

    // Call API in background
    try {
      await reorderComponents(reordered.map((c) => c.id));
      // Trigger parent refresh to sync with server
      onComponentsChange();
    } catch (error) {
      // Rollback on error
      setLocalComponents(components);
      toast.error('Failed to reorder components');
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
          Documents
        </h3>
      </div>

      {/* Component List or Add Panel */}
      <div className="flex-1">
        {showAddPanel ? (
          <div className="animate-in fade-in slide-in-from-right-3 duration-300">
            <AddComponentInline
              packageId={packageId}
              requestInfo={requestInfo}
              firmId={matter?.firm?.id ?? null}
              onAdd={async () => {
                await onComponentsChange();
                onShowAddPanel(false);
              }}
              onClose={() => onShowAddPanel(false)}
            />
          </div>
        ) : (
          <div>
            {localComponents.length > 0 ? (
              <div className="space-y-3">
                {/* Add Button at top of list - only in edit mode */}
                {isEditMode && (
                  <button
                    type="button"
                    onClick={() => onShowAddPanel(true)}
                    className="group w-full rounded-[18px] border-2 border-dashed border-zinc-300 bg-white/70 p-4 transition-all duration-200 hover:border-indigo-400 hover:bg-white active:scale-[0.99]"
                  >
                    <div className="flex items-center justify-center gap-2 text-zinc-500 group-hover:text-indigo-600">
                      <div className="rounded bg-zinc-100 p-1 transition-colors group-hover:bg-indigo-100">
                        <PlusIcon className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-sm font-medium">
                        Add Document
                      </span>
                    </div>
                  </button>
                )}

                {/* Component List */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={localComponents.map((c) => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {localComponents.map((component) => (
                        <ComponentItem
                          key={component.id}
                          component={component}
                          packageId={packageId}
                          requestInfo={requestInfo}
                          matterContext={matterContext}
                          isSelected={component.id === selectedComponentId}
                          onSelect={() =>
                            onSelectComponent(
                              component.id === selectedComponentId
                                ? null
                                : component.id,
                            )
                          }
                          onRemove={() => handleRemoveComponent(component.id)}
                          onUpdate={onComponentsChange}
                          disabled={isLoading}
                          packageMode={packageMode}
                          onOpenDocumentEditor={onOpenDocumentEditor}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            ) : (
              <EmptyState
                onAddClick={() => onShowAddPanel(true)}
                showAddButton={isEditMode}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Empty state component
 */
interface EmptyStateProps {
  onAddClick: () => void;
  showAddButton: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  onAddClick,
  showAddButton,
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-300">
      <div className="relative mb-3">
        <div className="w-12 h-12 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl flex items-center justify-center shadow-sm">
          <DocumentTextIcon className="w-6 h-6 text-indigo-500" />
        </div>
        <SparklesIcon className="w-4 h-4 text-indigo-400 absolute -top-1 -right-1" />
      </div>
      <p className="text-sm font-semibold text-gray-900 mb-1">
        {showAddButton ? 'No documents yet' : 'No documents yet'}
      </p>
      <p className="text-xs text-gray-500 mb-6 leading-relaxed">
        {showAddButton
          ? 'Add templates or documents to get started'
          : 'This package is empty'}
      </p>
      {showAddButton && (
        <Button
          onClick={onAddClick}
          color="indigo"
          className="text-xs h-8 px-4 shadow-sm hover:shadow transition-shadow"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Add Document
        </Button>
      )}
    </div>
  );
};

/**
 * Component type selection card
 */
interface ComponentTypeCardProps {
  type: 'template' | 'document' | 'static' | 'signedAuthorization';
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

const ComponentTypeCard: React.FC<ComponentTypeCardProps> = ({
  icon: Icon,
  title,
  description,
  onClick,
  disabled,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'w-full p-4 border-2 border-gray-200 rounded-xl',
      'hover:border-indigo-400 hover:bg-indigo-50/50 hover:shadow-sm',
      'active:scale-[0.98]',
      'transition-all duration-200',
      'text-left group',
      disabled && 'opacity-50 cursor-not-allowed',
    )}
    disabled={disabled}
  >
    <div className="flex items-center gap-3">
      <div className="p-2.5 bg-gray-100 rounded-xl group-hover:bg-indigo-100 transition-colors shadow-sm">
        <Icon className="h-5 w-5 text-gray-600 group-hover:text-indigo-600 transition-colors" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 text-sm truncate group-hover:text-indigo-900 transition-colors">
          {title}
        </p>
        <p className="text-xs text-gray-500 mt-1 truncate">{description}</p>
      </div>
      <ChevronLeftIcon className="h-4 w-4 text-gray-400 rotate-180 group-hover:translate-x-0.5 transition-transform" />
    </div>
  </button>
);

/**
 * Inline add component UI
 */
interface AddComponentInlineProps {
  packageId: string;
  requestInfo: PackageRequestInfo;
  onAdd: () => void;
  onClose: () => void;
  firmId?: string | null;
}

const AddComponentInline: React.FC<AddComponentInlineProps> = ({
  packageId,
  requestInfo,
  onAdd,
  onClose,
  firmId,
}) => {
  const [selectedType, setSelectedType] = useState<
    'template' | 'document' | 'static' | 'signedAuthorization' | null
  >(null);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [signedAuthorizationDocumentIds, setSignedAuthorizationDocumentIds] =
    useState<string[]>([]);
  const [selectedSignedTemplateId, setSelectedSignedTemplateId] = useState<
    string | null
  >(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [staticPreview, setStaticPreview] = useState<{
    name: string;
    url: string;
  } | null>(null);

  const handleRefetch = async () => {
    await onAdd();
  };

  const {
    addTemplate,
    addBlob,
    addStaticDocument,
    addSignedAuthorizationComponent,
    isLoading,
  } = usePackageComponents({
    packageId,
    requestInfo,
    components: [],
    onRefetch: handleRefetch,
  });

  const { data: firmDetails } = useGetFirmQuery(firmId ?? '', {
    skip: !firmId,
  });

  useEffect(() => {
    if (!requestInfo.matterId) return;
    const cached = getSignedAuthorizationSelection(requestInfo.matterId);
    if (!cached) return;

    setSignedAuthorizationDocumentIds((prev) =>
      prev.length > 0 ? prev : [cached.signedBlobId],
    );
    setSelectedSignedTemplateId((prev) => prev ?? cached.templateId);
  }, [requestInfo.matterId]);

  const staticDocumentPreviewUrls: Record<StaticDocumentTag, string | null> =
    useMemo(
      () => ({
        attorney_relationship:
          firmDetails?.hipaa_attorney_relationship_form_url ?? null,
        reproductive_rights:
          firmDetails?.hipaa_reproductive_rights_attestation_url ?? null,
        client_id: null,
        texas_affidavit: null,
      }),
      [
        firmDetails?.hipaa_attorney_relationship_form_url,
        firmDetails?.hipaa_reproductive_rights_attestation_url,
      ],
    );

  const showSuccessState = (message: string) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    // Auto-hide success and reset after 2 seconds
    setTimeout(() => {
      setShowSuccess(false);
      setSelectedType(null);
      setSelectedDocumentIds([]);
      setSignedAuthorizationDocumentIds([]);
      setSelectedSignedTemplateId(null);
    }, 1500);
  };

  const handleAddTemplate = async (templateId: string) => {
    try {
      await addTemplate(templateId);
      showSuccessState('Template added successfully!');
      toast.success('Template added to package');
    } catch (error) {
      toast.error('Failed to add template');
    }
  };

  const handleAddDocuments = async () => {
    try {
      await Promise.all(selectedDocumentIds.map((blobId) => addBlob(blobId)));
      const count = selectedDocumentIds.length;
      showSuccessState(
        `${count} document${count > 1 ? 's' : ''} added successfully!`,
      );
      toast.success(
        `${count} document${count > 1 ? 's' : ''} added to package`,
      );
    } catch (error) {
      toast.error('Failed to add documents');
    }
  };

  const handleAddStaticDocument = async (tag: StaticDocumentTag) => {
    try {
      await addStaticDocument(tag);
      showSuccessState('Firm document added successfully!');
      toast.success('Firm document added to package');
    } catch (error) {
      toast.error('Failed to add firm document');
    }
  };

  const handleSignedAuthorizationConfirm = async () => {
    if (signedAuthorizationDocumentIds.length === 0) {
      toast.error('Select a signed authorization PDF from matter files');
      return;
    }

    if (!selectedSignedTemplateId) {
      toast.error('Select a HIPAA authorization template (PDF)');
      return;
    }

    try {
      await addSignedAuthorizationComponent(
        selectedSignedTemplateId,
        signedAuthorizationDocumentIds[0],
      );
      setSignedAuthorizationSelection(requestInfo.matterId, {
        signedBlobId: signedAuthorizationDocumentIds[0],
        templateId: selectedSignedTemplateId,
      });
      showSuccessState('Signed authorization added successfully!');
      toast.success(
        'Signed authorization merge queued. Re-run Generate Package to refresh the preview.',
      );
    } catch (error) {
      const maybeError = error as {
        data?: { error?: string; detail?: string };
      };
      const message =
        maybeError?.data?.error ||
        maybeError?.data?.detail ||
        'Failed to add signed authorization merge';
      toast.error(message);
    }
  };

  const hipaaTemplateCategories: TemplateCategoryType[] = [
    'HIPAA_AUTHORIZATION',
    'HIPAA_COVERSHEET',
  ];

  const authorizationTemplateCategories: TemplateCategoryType[] = [
    'HIPAA_AUTHORIZATION',
  ];

  const signedAuthorizationDocumentFilter = (blob: BlobType) => {
    const filename = (blob.original_filename || blob.name || '').toLowerCase();
    return filename.endsWith('.pdf');
  };

  // Static document options
  const staticDocumentOptions = useMemo(
    () => [
      {
        tag: 'attorney_relationship' as StaticDocumentTag,
        name: 'Attorney Relationship Form',
        description: 'Document establishing attorney-client relationship',
        previewUrl: staticDocumentPreviewUrls.attorney_relationship,
      },
      {
        tag: 'reproductive_rights' as StaticDocumentTag,
        name: 'Reproductive Rights Attestation',
        description: 'Attestation regarding reproductive health information',
        previewUrl: staticDocumentPreviewUrls.reproductive_rights,
      },
      {
        tag: 'client_id' as StaticDocumentTag,
        name: 'Client ID Requirement',
        description: 'Standard client identification requirement form',
        previewUrl: staticDocumentPreviewUrls.client_id,
      },
      {
        tag: 'texas_affidavit' as StaticDocumentTag,
        name: 'Texas Affidavit',
        description:
          'Blank affidavit form for Texas medical records requests, filled out by the provider',
        previewUrl: staticDocumentPreviewUrls.texas_affidavit,
      },
    ],
    [staticDocumentPreviewUrls],
  );

  const handleStaticPreview = (docName: string, tag: StaticDocumentTag) => {
    const url = staticDocumentPreviewUrls[tag];
    if (!url) {
      toast.error('No preview available for this document');
      return;
    }
    setStaticPreview({ name: docName, url });
  };

  const handleBack = () => {
    if (selectedType) {
      // If viewing a specific type, go back to type selection
      setSelectedType(null);
      setSelectedDocumentIds([]);
      setSignedAuthorizationDocumentIds([]);
      setSelectedSignedTemplateId(null);
    } else {
      // If at type selection, close the add panel
      onClose();
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Success State */}
      {showSuccess ? (
        <div className="flex-1 flex items-center justify-center animate-in fade-in zoom-in duration-300">
          <div className="text-center max-w-xs px-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircleIcon className="w-10 h-10 text-green-600" />
            </div>
            <p className="text-base font-semibold text-gray-900 mb-2">
              {successMessage}
            </p>
            <p className="text-sm text-gray-500">
              Returning to add more documents...
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Simple Back Button */}
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4 -ml-1 px-1 py-1 w-fit rounded-md hover:bg-gray-100 transition-all"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            <span className="font-medium">Back</span>
          </button>

          {/* Content */}
          <div className="flex-1 overflow-y-auto -mx-4 px-4">
            {!selectedType ? (
              <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <ComponentTypeCard
                  type="template"
                  icon={DocumentTextIcon}
                  title="HIPAA Template (DOCX)"
                  description="Auto-filled merge fields"
                  onClick={() => setSelectedType('template')}
                  disabled={isLoading}
                />
                <ComponentTypeCard
                  type="signedAuthorization"
                  icon={SparklesIcon}
                  title="Signed Authorization (PDF)"
                  description="Pre-signed authorization"
                  onClick={() => setSelectedType('signedAuthorization')}
                  disabled={isLoading}
                />
                <ComponentTypeCard
                  type="document"
                  icon={FolderIcon}
                  title="Matter Documents"
                  description="From this matter"
                  onClick={() => setSelectedType('document')}
                  disabled={isLoading}
                />
                <ComponentTypeCard
                  type="static"
                  icon={BuildingOffice2Icon}
                  title="Firm Documents"
                  description="Standard forms"
                  onClick={() => setSelectedType('static')}
                  disabled={isLoading}
                />
              </div>
            ) : (
              <div className="animate-in fade-in slide-in-from-right-2 duration-300">
                {/* Template Selection */}
                {selectedType === 'template' && (
                  <TemplateSearchList
                    requestInfo={requestInfo}
                    onSelectTemplate={handleAddTemplate}
                    isLoading={isLoading}
                    includeCategories={hipaaTemplateCategories}
                    allowedTemplateTypes={['DOCX', 'HTML']}
                    enablePreview
                    confirmButtonLabel="Add Template"
                  />
                )}

                {/* Document Selection */}
                {selectedType === 'document' && requestInfo.matterId && (
                  <DocumentSelector
                    matterId={requestInfo.matterId}
                    selectedBlobIds={selectedDocumentIds}
                    onSelectionChange={setSelectedDocumentIds}
                    onConfirm={handleAddDocuments}
                    multiSelect
                    enablePreview
                    previewDialogTitle="Matter Document Preview"
                  />
                )}

                {/* Signed Authorization Merge */}
                {selectedType === 'signedAuthorization' &&
                  requestInfo.matterId && (
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-gray-700">
                          1. Choose Signed Authorization PDF
                        </h4>
                        <p className="text-xs text-gray-500">
                          Select the signed authorization from matter documents
                          (PDF only). Use &quot;Extract&quot; to pull pages from
                          a larger document.
                        </p>
                        <DocumentSelector
                          matterId={requestInfo.matterId}
                          selectedBlobIds={signedAuthorizationDocumentIds}
                          onSelectionChange={setSignedAuthorizationDocumentIds}
                          onConfirm={() => undefined}
                          filterBlobs={signedAuthorizationDocumentFilter}
                          multiSelect={false}
                          showConfirmButton={false}
                          enablePreview
                          previewDialogTitle="Signed Authorization Preview"
                          enableExtraction
                          extractionContext="signed-auth"
                        />
                      </div>

                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-gray-700">
                          2. Choose Authorization Template (PDF)
                        </h4>
                        <p className="text-xs text-gray-500">
                          Pick the firm authorization template that will merge
                          with the signed PDF.
                        </p>
                        <TemplateSearchList
                          requestInfo={requestInfo}
                          onSelectTemplate={(templateId) => {
                            setSelectedSignedTemplateId(templateId);
                            toast.success('Authorization template selected');
                          }}
                          isLoading={isLoading}
                          includeCategories={authorizationTemplateCategories}
                          allowedTemplateTypes={['PDF']}
                          enablePreview
                          autoConfirmOnSelect
                        />
                      </div>

                      <div className="sticky bottom-0 pt-4 border-t border-gray-200 bg-gray-50">
                        <Button
                          onClick={handleSignedAuthorizationConfirm}
                          color="light"
                          className="w-full shadow-sm text-gray-700 [--btn-bg:var(--color-zinc-100)] [--btn-border:var(--color-zinc-200)]"
                          disabled={
                            isLoading ||
                            signedAuthorizationDocumentIds.length === 0 ||
                            !selectedSignedTemplateId
                          }
                        >
                          Save Selections
                        </Button>
                        <p className="mt-2 text-xs text-gray-500 text-center">
                          We queue the merge immediately; run Generate Package
                          to pull the latest merged authorization into the
                          preview.
                        </p>
                      </div>
                    </div>
                  )}

                {/* Static Document Selection */}
                {selectedType === 'static' && (
                  <div className="space-y-3">
                    {staticDocumentOptions.map((doc) => (
                      <button
                        key={doc.tag}
                        type="button"
                        onClick={() => handleAddStaticDocument(doc.tag)}
                        className={cn(
                          'w-full p-4 border-2 rounded-xl text-left',
                          'hover:bg-gray-50 hover:border-indigo-300 hover:shadow-sm',
                          'active:scale-[0.98]',
                          'transition-all duration-200',
                          'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1',
                          isLoading && 'opacity-50 cursor-not-allowed',
                        )}
                        disabled={isLoading}
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <DocumentTextIcon className="h-5 w-5 text-gray-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-gray-900 mb-1">
                              {doc.name}
                            </p>
                            <p className="text-xs text-gray-500 leading-relaxed">
                              {doc.description}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <ChevronLeftIcon className="h-4 w-4 text-gray-400 rotate-180 flex-shrink-0" />
                            <button
                              type="button"
                              className={cn(
                                'text-[11px] font-semibold text-indigo-600 hover:text-indigo-800',
                                !doc.previewUrl &&
                                  'text-gray-400 cursor-not-allowed hover:text-gray-400',
                              )}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (doc.previewUrl) {
                                  handleStaticPreview(doc.name, doc.tag);
                                } else {
                                  toast.error(
                                    'Preview unavailable for this document',
                                  );
                                }
                              }}
                              disabled={!doc.previewUrl}
                            >
                              Preview
                            </button>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {staticPreview && (
        <Dialog
          open
          onClose={() => setStaticPreview(null)}
          zIndex={Z_INDEX_MODAL_DEEPLY_NESTED}
          nested
        >
          <DialogTitle>{staticPreview.name}</DialogTitle>
          <div className="mt-4 h-[70vh]">
            <iframe
              title={`${staticPreview.name} preview`}
              src={`${staticPreview.url}#toolbar=0`}
              className="w-full h-full rounded border"
            />
          </div>
          <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
            <span className="truncate pr-4">{staticPreview.name}</span>
            <button
              type="button"
              onClick={() =>
                window.open(staticPreview.url, '_blank', 'noopener,noreferrer')
              }
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Open in new tab
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
};
